import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  List,
  Tag,
  Typography,
  Space,
  Skeleton,
  Form,
  Input,
  Alert,
  Tooltip,
} from "antd";
import {
  KeyOutlined,
  EditOutlined,
  CheckCircleFilled,
  ExclamationCircleFilled,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Button, Modal, PageHeader } from "../../components/Common";
import { useMutations } from "../../hooks/useApiMutation";
import { useConfirm } from "../../hooks/useConfirm";
import { providerMeta } from "../../hooks/useLlmProviders";

const { Text } = Typography;



/**
 * Admin screen for rotating the encrypted LLM provider keys. The raw key is sent
 * over the wire once (inside the HTTPS body) and is encrypted server-side with
 * ENCRYPTBYPASSPHRASE before it is written to the DB. The key is never returned
 * by the API — this screen only shows whether a key is set.
 */
const LlmKeysList = () => {
  const { mutate } = useMutations();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;
  const { confirmAction } = useConfirm();

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await mutateRef.current("fetchLlmProviders", {
        method: "get",
        endpoint: "/v1/llm-models/providers",
      });
      setProviders(data?.data || []);
    } catch {
      setProviders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onRotate = (provider) => {
    setEditing(provider);
    form.resetFields();
    setOpen(true);
  };

  const onSubmit = async () => {
    const values = await form.validateFields();
    await mutateRef.current("updateLlmApiKey", {
      method: "put",
      endpoint: `/v1/llm-models/providers/${editing.provider}/api-key`,
      payload: { apiKey: values.apiKey },
      alert: { success: `${editing.provider} API key saved.` },
    });
    setOpen(false);
    load();
  };

  const onClear = (provider) =>
    confirmAction({
      key: "clearLlmApiKey",
      method: "put",
      endpoint: `/v1/llm-models/providers/${provider.provider}/api-key`,
      payload: { apiKey: null },
      title: `Remove ${provider.provider} API key`,
      content: `This will stop all ${provider.provider} calls until a new key is set. Continue?`,
      okText: "Remove",
      okType: "danger",
      onSuccess: load,
    });

  return (
    <div>
      <PageHeader
        title="LLM provider keys"
        subtitle={
          <>
            Keys are encrypted at rest with{" "}
            <Text code>ENCRYPTBYPASSPHRASE</Text> using the platform passphrase.
            They are never returned by the API — only the "is it set?" flag is
            visible here.
          </>
        }
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="This screen only rotates the API keys. Models are managed on the LLM Models screen."
      />

      {loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3 }}
          dataSource={providers}
          renderItem={(p) => {
            const meta = providerMeta(p.provider);
            return (
              <List.Item>
                <Card
                  size="small"
                  title={
                    <Space>
                      <KeyOutlined />
                      <span>{meta.label}</span>
                      <Tag color={meta.color}>{p.provider}</Tag>
                    </Space>
                  }
                  extra={
                    p.hasApiKey ? (
                      <Tooltip title="API key configured">
                        <CheckCircleFilled style={{ color: "#52c41a" }} />
                      </Tooltip>
                    ) : (
                      <Tooltip title="No key set — calls to this provider will fail">
                        <ExclamationCircleFilled style={{ color: "#faad14" }} />
                      </Tooltip>
                    )
                  }
                  actions={[
                    <Button
                      variant="ghost"
                      icon={<EditOutlined />}
                      onClick={() => onRotate(p)}
                    >
                      {p.hasApiKey ? "Rotate" : "Set key"}
                    </Button>,
                    p.hasApiKey ? (
                      <Button variant="ghost" danger onClick={() => onClear(p)}>
                        Clear
                      </Button>
                    ) : null,
                  ].filter(Boolean)}
                >
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%" }}
                  >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {p.modelCount} model{p.modelCount === 1 ? "" : "s"} available
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      <Space size={4}>
                        {p.hasApiKey ? (
                          <Tag color="success" style={{ margin: 0 }}>
                            Configured
                          </Tag>
                        ) : (
                          <Tag color="warning" style={{ margin: 0 }}>
                            Not configured
                          </Tag>
                        )}
                        <Tooltip title="We only store a one-way encrypted blob. The raw key cannot be retrieved from the database.">
                          <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
                        </Tooltip>
                      </Space>
                    </Text>
                  </Space>
                </Card>
              </List.Item>
            );
          }}
        />
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={onSubmit}
        title={editing ? `Set ${editing.provider} API key` : "Set API key"}
        confirmText="Save"
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="apiKey"
            label="API key"
            rules={[{ required: true, message: "Paste the provider key" }]}
            extra="Encrypted with ENCRYPTBYPASSPHRASE before storage. Never displayed again."
          >
            <Input.Password
              placeholder={
                editing?.provider === "OpenAI"
                  ? "sk-proj-…"
                  : editing?.provider === "Claude"
                  ? "sk-ant-api03-…"
                  : editing?.provider === "Kimi"
                  ? "sk-… (Moonshot, platform.kimi.ai)"
                  : "Provider API key"
              }
              allowClear
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LlmKeysList;
